import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchSprintBoard, completeSprint, startSprint, pauseSprint, resumeSprint,
  addItemToSprint, removeItemFromSprint, SprintStatus,
} from '../../../store/sprintSlice';
import { projectAPI, featureAPI, taskAPI, stageGateAPI } from '../../../api/services';
import api from '../../../api/axios';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import IncompleteItemsModal from '../Sprints/IncompleteItemsModal';
import StageGateDialog from './StageGateDialog';

// ─── Status constants (matching backend enums) ─────────────────────
const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'review', 'done'] as const;

// Bug status → kanban column mapping (used by backend getSprintBoard)
// Frontend only needs the reverse: COL_TO_BUG_STATUS for drag-drop
const COL_TO_BUG_STATUS: Record<string, string> = {
  todo: 'reported', in_progress: 'in_progress', blocked: 'in_progress', review: 'testing', done: 'fixed',
};

const FEATURE_STATUS_TO_COL: Record<string, string> = {
  planning: 'todo',
  development: 'in_progress',
  testing: 'review',
  done: 'done', deployed: 'done', archived: 'done',
};
const COL_TO_FEATURE_STATUS: Record<string, string> = {
  todo: 'planning', in_progress: 'development', blocked: 'development', review: 'testing', done: 'done',
};

const COLUMNS = TASK_STATUSES;
const COL_LABELS: Record<string, string> = { todo: 'To Do', in_progress: 'In Progress', blocked: 'Blocked', review: 'Review', done: 'Done' };
const COL_HEADER_BG: Record<string, string> = { todo: 'bg-gray-50', in_progress: 'bg-blue-50', blocked: 'bg-red-50', review: 'bg-yellow-50', done: 'bg-green-50' };
const SPRINT_STATUS_COLORS: Record<SprintStatus, string> = { planned: 'bg-gray-100 text-gray-700', active: 'bg-green-100 text-green-700', paused: 'bg-yellow-100 text-yellow-700', completed: 'bg-blue-100 text-blue-700', cancelled: 'bg-red-100 text-red-700' };
const PRIORITY_PILL: Record<string, string> = { critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white', medium: 'bg-yellow-400 text-gray-900', low: 'bg-gray-200 text-gray-600' };
const STATUS_DOT: Record<string, string> = { todo: 'bg-gray-400', in_progress: 'bg-blue-500', blocked: 'bg-red-500', review: 'bg-yellow-500', done: 'bg-green-500' };

// ─── Types ──────────────────────────────────────────────────────────
type BoardItem = {
  _id: string;
  title: string;
  status: string;
  _itemType?: 'bug' | 'feature';
  _col: string;
  priority?: string;
  storyPoints?: number;
  assignedTo?: any[];
  parentType?: string;
  parentId?: string;
};

// ─── Draggable row cell ─────────────────────────────────────────────
const DraggableCell: React.FC<{
  dragId: string;
  children: React.ReactNode;
}> = ({ dragId, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: dragId });
  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1, position: 'relative', zIndex: isDragging ? 50 : undefined }
    : {};
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

// ─── Droppable cell (unique ID per cell) ───────────────────────────
const DroppableCell: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`overflow-hidden transition-colors ${isOver ? 'bg-blue-50/60' : ''}`}>
      {children}
    </div>
  );
};

// ─── Unassigned Panel ────────────────────────────────────────────
const UnassignedPanel: React.FC<{
  items: any[];
  features: any[];
  onAdd: (itemId: string, itemType: string) => void;
  locked: boolean;
}> = ({ items, features, onAdd, locked }) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const totalUnassigned = items.length + features.filter(f => !f.sprintId).length;
  const [expanded, setExpanded] = useState(totalUnassigned > 0);

  // Auto-collapse when empty, auto-expand when items appear
  useEffect(() => {
    if (totalUnassigned === 0) setExpanded(false);
    else setExpanded(true);
  }, [totalUnassigned > 0]);
  const toggle = (k: string) => setCollapsed(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const featureGroups: Record<string, { feature: any; tasks: any[] }> = {};
  const standalone: any[] = [];
  for (const t of items) {
    if (t.parentType === 'feature' && t.parentId) {
      const f = features.find((ft: any) => ft._id === t.parentId);
      if (f) { if (!featureGroups[t.parentId]) featureGroups[t.parentId] = { feature: f, tasks: [] }; featureGroups[t.parentId].tasks.push(t); continue; }
    }
    standalone.push(t);
  }
  const emptyFeatures = features.filter(f => !f.sprintId && !featureGroups[f._id]);

  return (
    <div
      className="border-r border-gray-200 bg-white flex flex-col shrink-0 overflow-hidden transition-all duration-300 ease-in-out"
      style={{ width: expanded ? 240 : 36 }}
    >
      {/* Header — always visible */}
      <div
        className={`border-b border-gray-200 flex items-center cursor-pointer shrink-0 ${expanded ? 'px-3 py-2.5 justify-between' : 'px-0 py-2.5 justify-center'}`}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <>
            <div className="flex items-center gap-2">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              <span className="text-sm font-semibold text-gray-600">Unassigned</span>
            </div>
            <span className="text-xs text-gray-400">{items.length + emptyFeatures.length}</span>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1" title="Show unassigned items">
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-[10px] text-gray-400 font-medium">{items.length + emptyFeatures.length}</span>
          </div>
        )}
      </div>

      {/* Content — rendered always but hidden via overflow when collapsed */}
      <div className={`flex-1 overflow-y-auto ${expanded ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-200`}>
        {Object.entries(featureGroups).map(([fId, { feature: f, tasks }]) => (
          <div key={fId}>
            <div className="flex items-center gap-1.5 px-3 py-1.5 cursor-pointer hover:bg-blue-50/50" onClick={() => toggle(fId)}>
              <svg className={`w-3 h-3 text-blue-400 transition-transform ${!collapsed.has(fId) ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
              <span className="text-xs font-medium text-blue-700 truncate flex-1">{f.name}</span>
              {!locked && (
                <button onClick={e => { e.stopPropagation(); onAdd(f._id, 'feature'); }} className="text-xs text-blue-600 hover:text-blue-700 shrink-0">Add all</button>
              )}
            </div>
            {!collapsed.has(fId) && tasks.map((t: any) => (
              <div key={t._id} className="flex items-center gap-2 px-3 py-1.5 pl-8 hover:bg-gray-50 text-xs">
                <div className={`w-2 h-2 rounded-full shrink-0 ${t.status === 'done' ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="truncate flex-1 text-gray-700" title={t.title}>{t.title}</span>
                {!locked && <button onClick={() => onAdd(t._id, 'task')} className="text-blue-600 hover:text-blue-700 shrink-0">+ Add</button>}
              </div>
            ))}
          </div>
        ))}

        {emptyFeatures.map((f: any) => (
          <div key={f._id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50/50 text-xs">
            <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
            <span className="truncate flex-1 text-blue-700">{f.name}</span>
            {!locked && <button onClick={() => onAdd(f._id, 'feature')} className="text-blue-600 hover:text-blue-700 shrink-0">+ Add</button>}
          </div>
        ))}

        {standalone.map((t: any) => (
          <div key={t._id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-xs">
            <div className={`w-2 h-2 rounded-full shrink-0 ${t.status === 'done' ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="truncate flex-1 text-gray-700" title={t.title}>{t.title}</span>
            {!locked && <button onClick={() => onAdd(t._id, 'task')} className="text-blue-600 hover:text-blue-700 shrink-0">+ Add</button>}
          </div>
        ))}

        {items.length === 0 && emptyFeatures.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-4">All items assigned</p>
        )}
      </div>
    </div>
  );
};

// ─── Main SprintKanban ───────────────────────────────────────────
interface StageGateConfig {
  stage: string;
  gateType: 'approval' | 'testing';
  mandatory: boolean;
  defaultApprovers?: string[];
}

interface Props {
  projectId: string;
  sprintId: string;
  onBack: () => void;
  stageGates?: StageGateConfig[];
  projectMembers?: { userId: string; name: string }[];
}

const SprintKanban: React.FC<Props> = ({ projectId, sprintId, onBack, stageGates = [], projectMembers = [] }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSprint, board, features: sprintFeatures, incompleteItems } = useAppSelector(s => s.sprints);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [gateDialog, setGateDialog] = useState<{ taskId: string; taskTitle: string; targetCol: string; gates: StageGateConfig[] } | null>(null);

  // Expanded features + feature order — persisted to backend
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [featureOrder, setFeatureOrder] = useState<string[]>([]);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Load preferences from backend on mount
  useEffect(() => {
    api.get(`/sprints/${sprintId}/preferences`).then(res => {
      const data = res.data?.data || {};
      if (data.expandedFeatures?.length) setExpandedFeatures(new Set(data.expandedFeatures));
      if (data.featureOrder?.length) setFeatureOrder(data.featureOrder);
      setPrefsLoaded(true);
    }).catch(() => setPrefsLoaded(true));
  }, [sprintId]);

  // Debounced save to backend + sync sidebar
  const saveTimerRef = useCallback(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (expanded: string[], order: string[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        api.put(`/sprints/${sprintId}/preferences`, { expandedFeatures: expanded, featureOrder: order }).catch(() => {});
      }, 500);
    };
  }, [sprintId])();

  const persistPrefs = useCallback((expanded: Set<string>, order: string[]) => {
    const arr = [...expanded];
    // Also sync via event so SprintTree sidebar updates
    window.dispatchEvent(new CustomEvent('sprint-features-changed', { detail: { sprintId, expanded: arr, order } }));
    saveTimerRef(arr, order);
  }, [sprintId, saveTimerRef]);

  // Sync from sidebar
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sprintId === sprintId && detail?.expanded) {
        setExpandedFeatures(new Set(detail.expanded));
        if (detail.order) setFeatureOrder(detail.order);
      }
    };
    window.addEventListener('sprint-features-changed', handler);
    return () => window.removeEventListener('sprint-features-changed', handler);
  }, [sprintId]);

  // Optimistic column overrides: id → targetCol (applied instantly on drop, cleared on re-fetch)
  const [colOverrides, setColOverrides] = useState<Record<string, string>>({});
  const [statusError, setStatusError] = useState<string | null>(null);

  const [unassignedTasks, setUnassignedTasks] = useState<any[]>([]);
  const [allFeatures, setAllFeatures] = useState<any[]>([]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchBoard = useCallback(() => {
    dispatch(fetchSprintBoard({ id: sprintId })).then(() => setColOverrides({}));
  }, [dispatch, sprintId]);
  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const fetchUnassigned = useCallback(async () => {
    try {
      const [tasksRes, featuresRes] = await Promise.all([
        projectAPI.getBacklog(projectId),
        featureAPI.getByProject(projectId),
      ]);
      const backlog = tasksRes.data.data ?? tasksRes.data ?? {};
      setUnassignedTasks(Array.isArray(backlog.tasks) ? backlog.tasks : Array.isArray(backlog) ? backlog : []);
      setAllFeatures(Array.isArray(featuresRes.data.data) ? featuresRes.data.data : []);
    } catch { setUnassignedTasks([]); setAllFeatures([]); }
  }, [projectId]);
  useEffect(() => { fetchUnassigned(); }, [fetchUnassigned]);

  const handleAddToSprint = async (itemId: string, itemType: string) => {
    try {
      await dispatch(addItemToSprint({ id: sprintId, itemId, itemType }));
      fetchBoard();
      fetchUnassigned();
    } catch { /* ignore */ }
  };

  const handleRemoveFromSprint = async (itemId: string, itemType: string) => {
    try {
      await dispatch(removeItemFromSprint({ id: sprintId, itemId, itemType }));
      fetchBoard();
      fetchUnassigned();
    } catch { /* ignore */ }
  };

  const toggleFeature = (fId: string) => {
    setExpandedFeatures(prev => {
      const n = new Set(prev); n.has(fId) ? n.delete(fId) : n.add(fId);
      persistPrefs(n, featureOrder);
      return n;
    });
  };

  // ─── Build feature sections with per-column task lists ──────
  // Each feature is a horizontal band. Within each band, tasks
  // stack per-column independently. Standalone items form their own band.
  type FeatureSection = {
    id: string; // feature _id
    title: string;
    taskCount: number;
    sp: number;
    fCol: string; // feature header's column
    tasksByCol: Record<string, BoardItem[]>; // col → tasks in that col for this feature
  };

  const { featureSections, standalonByCol, itemLookup } = useMemo(() => {
    const sections: FeatureSection[] = [];
    const standaloneByCol: Record<string, BoardItem[]> = {};
    for (const col of COLUMNS) standaloneByCol[col] = [];
    const lookup = new Map<string, { featureId?: string; item?: BoardItem; col: string; type: string }>();

    if (!board) return { featureSections: sections, standalonByCol: standaloneByCol, itemLookup: lookup };

    const featureMap: Record<string, any> = {};
    for (const f of (sprintFeatures || [])) featureMap[f._id] = f;

    // Collect items with effective column
    const allItems: BoardItem[] = [];
    for (const [col, items] of Object.entries(board)) {
      for (const item of (items as any[])) {
        const effectiveCol = colOverrides[item._id] || col;
        allItems.push({ ...item, _col: effectiveCol });
      }
    }

    // Group by feature
    const featureGroups: Record<string, { feature: any; tasks: BoardItem[] }> = {};
    const standalone: BoardItem[] = [];

    for (const item of allItems) {
      if (item.parentType === 'feature' && item.parentId && featureMap[item.parentId]) {
        if (!featureGroups[item.parentId]) featureGroups[item.parentId] = { feature: featureMap[item.parentId], tasks: [] };
        featureGroups[item.parentId].tasks.push(item);
      } else {
        standalone.push(item);
      }
    }

    for (const f of (sprintFeatures || [])) {
      if (!featureGroups[f._id]) featureGroups[f._id] = { feature: f, tasks: [] };
    }

    // Build sections
    for (const [fId, group] of Object.entries(featureGroups)) {
      const f = group.feature;
      const fCol = colOverrides[`fh:${fId}`] || FEATURE_STATUS_TO_COL[f.status] || 'todo';
      const taskSP = group.tasks.reduce((s, t) => s + (t.storyPoints || 0), 0);
      const totalSP = Math.max(taskSP, f.storyPoints || 0);

      const tasksByCol: Record<string, BoardItem[]> = {};
      for (const col of COLUMNS) tasksByCol[col] = [];
      for (const task of group.tasks) {
        if (tasksByCol[task._col]) tasksByCol[task._col].push(task);
        lookup.set(task._id, { featureId: fId, item: task, col: task._col, type: task._itemType === 'bug' ? 'bug' : 'task' });
      }

      lookup.set(`fh:${fId}`, { featureId: fId, col: fCol, type: 'feature-header' });

      sections.push({ id: fId, title: f.name || f.title, taskCount: group.tasks.length, sp: totalSP, fCol, tasksByCol });
    }

    // Standalone
    for (const item of standalone) {
      if (standaloneByCol[item._col]) standaloneByCol[item._col].push(item);
      lookup.set(item._id, { item, col: item._col, type: item._itemType === 'bug' ? 'bug' : 'task' });
    }

    // Sort sections by persisted featureOrder
    if (featureOrder.length > 0) {
      const orderMap = new Map(featureOrder.map((id, i) => [id, i]));
      sections.sort((a, b) => {
        const ai = orderMap.get(a.id) ?? 9999;
        const bi = orderMap.get(b.id) ?? 9999;
        return ai - bi;
      });
    }

    return { featureSections: sections, standalonByCol: standaloneByCol, itemLookup: lookup };
  }, [board, sprintFeatures, colOverrides, featureOrder]);

  // Auto-persist feature order when sections first load and no order exists
  useEffect(() => {
    if (featureSections.length > 0 && featureOrder.length === 0 && prefsLoaded) {
      const order = featureSections.map(s => s.id);
      setFeatureOrder(order);
      persistPrefs(expandedFeatures, order);
    }
  }, [featureSections.length, featureOrder.length, prefsLoaded]);

  // ─── Drag handlers ──────────────────────────────────────────
  const handleDragStart = (e: DragStartEvent) => setActiveDragId(e.active.id as string);

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over) return;

    const dragId = active.id as string;
    const overId = over.id as string;
    // over.id is "col:featureId" or "col:standalone" — extract column
    const targetCol = overId.split(':')[0];
    if (!(COLUMNS as readonly string[]).includes(targetCol)) return;

    const entry = itemLookup.get(dragId);
    if (!entry || entry.col === targetCol) return;

    const isFeatureDrag = dragId.startsWith('fh:');

    if (isFeatureDrag) {
      const featureId = dragId.replace('fh:', '');
      const section = featureSections.find(s => s.id === featureId);
      if (!section) return;

      // Optimistic: move header + all tasks
      const overrides: Record<string, string> = { [dragId]: targetCol };
      for (const tasks of Object.values(section.tasksByCol)) {
        for (const t of tasks) overrides[t._id] = targetCol;
      }
      setColOverrides(prev => ({ ...prev, ...overrides }));

      // API
      const updates: Promise<any>[] = [];
      const newStatus = COL_TO_FEATURE_STATUS[targetCol];
      if (newStatus) updates.push(featureAPI.update(featureId, { status: newStatus } as any));
      for (const [col, tasks] of Object.entries(section.tasksByCol)) {
        if (col === targetCol) continue;
        for (const t of tasks) {
          if (t._itemType === 'bug') {
            const bs = COL_TO_BUG_STATUS[targetCol];
            if (bs) updates.push(api.put(`/bugs/${t._id}/status`, { status: bs }));
          } else {
            updates.push(taskAPI.updateStatus(t._id, targetCol));
          }
        }
      }
      Promise.all(updates).catch((err: any) => {
        const msg = err?.response?.data?.message;
        if (msg) { setStatusError(msg); setTimeout(() => setStatusError(null), 5000); }
      }).finally(() => fetchBoard());
    } else {
      if (!entry.item) return;

      // Check stage gates for individual task drags (not bugs)
      if (entry.type !== 'bug') {
        const matchingGates = stageGates.filter(g => g.stage === targetCol);
        if (matchingGates.length > 0) {
          try {
            const existingRes = await stageGateAPI.getTaskGates(entry.item._id);
            const existingGates = existingRes.data?.data || [];
            const stageGatesForTarget = existingGates.filter((g: any) => g.stage === targetCol);
            const allCleared = stageGatesForTarget.length > 0 && stageGatesForTarget.every((g: any) =>
              g.status === 'approved' || g.status === 'passed' || g.status === 'skipped'
            );
            if (!allCleared) {
              setGateDialog({
                taskId: entry.item._id,
                taskTitle: entry.item.title || 'Task',
                targetCol,
                gates: matchingGates,
              });
              return;
            }
          } catch {
            setGateDialog({
              taskId: entry.item._id,
              taskTitle: entry.item.title || 'Task',
              targetCol,
              gates: matchingGates,
            });
            return;
          }
        }
      }

      setColOverrides(prev => ({ ...prev, [dragId]: targetCol }));
      const apiCall = entry.type === 'bug'
        ? api.put(`/bugs/${entry.item._id}/status`, { status: COL_TO_BUG_STATUS[targetCol] })
        : taskAPI.updateStatus(entry.item._id, targetCol);
      apiCall.catch((err: any) => {
        setColOverrides(prev => { const n = { ...prev }; delete n[dragId]; return n; });
        const msg = err?.response?.data?.message;
        if (msg) { setStatusError(msg); setTimeout(() => setStatusError(null), 5000); }
      }).finally(() => fetchBoard());
    }
  };

  const completeGatedMove = (taskId: string, targetCol: string) => {
    setColOverrides(prev => ({ ...prev, [taskId]: targetCol }));
    taskAPI.updateStatus(taskId, targetCol)
      .catch(() => {
        setColOverrides(prev => { const n = { ...prev }; delete n[taskId]; return n; });
      })
      .finally(() => fetchBoard());
  };

  const handleLifecycle = async (action: string) => {
    switch (action) {
      case 'start': await dispatch(startSprint(sprintId)); break;
      case 'complete': {
        const result = await dispatch(completeSprint(sprintId));
        if (completeSprint.fulfilled.match(result) && (result.payload.incompleteItems?.length ?? 0) > 0) setShowIncomplete(true);
        break;
      }
      case 'pause': await dispatch(pauseSprint({ id: sprintId })); break;
      case 'resume': await dispatch(resumeSprint({ id: sprintId })); break;
    }
    fetchBoard();
  };

  // ─── Render a task/bug row ──────────────────────────────────
  const renderTaskItem = (item: BoardItem, type: string, indented: boolean) => (
    <DraggableCell dragId={item._id}>
      <div
        className={`group/row px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-50 transition-colors border-b border-gray-100 ${indented ? 'pl-5' : ''}`}
        onClick={() => navigate(type === 'bug' ? `/employee/bugs/${item._id}` : `/employee/tasks/${item._id}`)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[item._col] || STATUS_DOT[item.status] || 'bg-gray-300'}`} />
          {type === 'bug' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 shrink-0">BUG</span>}
          <span className={`text-sm truncate flex-1 ${item.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`} title={item.title}>{item.title}</span>
          {item.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${PRIORITY_PILL[item.priority] || 'bg-gray-200 text-gray-600'}`}>{item.priority}</span>}
          {(item.storyPoints ?? 0) > 0 && <span className="text-xs text-blue-600 font-medium shrink-0">{item.storyPoints}</span>}
          {(item.assignedTo ?? []).length > 0 && (
            <div className="flex -space-x-1 shrink-0">
              {(item.assignedTo ?? []).slice(0, 2).map((a: any, i: number) => (
                <div key={i} className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center border border-white" title={a.name}>{(a.name || '?')[0]}</div>
              ))}
            </div>
          )}
          {!isLocked && (
            <button onClick={e => { e.stopPropagation(); handleRemoveFromSprint(item._id, type === 'bug' ? 'bug' : 'task'); }} onPointerDown={e => e.stopPropagation()} className="text-gray-300 hover:text-red-500 shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity" title="Return to unassigned">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
            </button>
          )}
        </div>
      </div>
    </DraggableCell>
  );

  const getDragOverlayContent = () => {
    if (!activeDragId) return null;
    const entry = itemLookup.get(activeDragId);
    if (!entry) return null;

    if (entry.type === 'feature-header') {
      const section = featureSections.find(s => s.id === entry.featureId);
      return (
        <div className="bg-blue-50 border border-blue-300 px-3 py-2 shadow-lg rotate-1 min-w-[200px]">
          <div className="flex items-center gap-2">
            <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
            <span className="text-sm font-medium text-blue-700">{section?.title}</span>
            <span className="text-xs text-gray-400">{section?.taskCount} tasks</span>
          </div>
        </div>
      );
    }
    return (
      <div className="bg-white border border-blue-300 px-3 py-2 shadow-lg rotate-1 min-w-[200px]">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[entry.col] || 'bg-gray-300'}`} />
          {entry.type === 'bug' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">BUG</span>}
          <span className="text-sm text-gray-800 truncate">{entry.item?.title}</span>
        </div>
      </div>
    );
  };

  const s = currentSprint;
  const isLocked = s?.status === 'completed' || s?.status === 'cancelled';
  const completionPct = s && s.stats.totalTasks > 0 ? Math.round((s.stats.completedTasks / s.stats.totalTasks) * 100) : 0;

  const totalTasks = useMemo(() => {
    let n = 0;
    for (const col of COLUMNS) n += (standalonByCol[col] || []).length;
    for (const sec of featureSections) for (const tasks of Object.values(sec.tasksByCol)) n += tasks.length;
    return n;
  }, [featureSections, standalonByCol]);

  if (!s && !board) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 bg-white border-b border-gray-200 shrink-0">
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  const hasStandalone = COLUMNS.some(c => (standalonByCol[c] || []).length > 0);

  return (
    <div className="flex flex-col h-full">
      {/* Sprint header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900">{s?.name}</h2>
                {s && <span className={`text-xs px-2 py-0.5 rounded-full ${SPRINT_STATUS_COLORS[s.status]}`}>{s.status}</span>}
                {s?.sprintType && <span className="text-xs text-gray-400 capitalize">{s.sprintType}</span>}
              </div>
              {s && s.stats.totalTasks > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${completionPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${completionPct}%` }} />
                  </div>
                  <span className="text-xs text-gray-400">{s.stats.completedTasks}/{s.stats.totalTasks} · {completionPct}%</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {s?.status === 'planned' && <button onClick={() => handleLifecycle('start')} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700">Start Sprint</button>}
            {s?.status === 'active' && (
              <>
                <button onClick={() => handleLifecycle('pause')} className="text-xs border border-yellow-300 text-yellow-700 px-3 py-1.5 rounded-lg hover:bg-yellow-50">Pause</button>
                <button onClick={() => handleLifecycle('complete')} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">Complete</button>
              </>
            )}
            {s?.status === 'paused' && <button onClick={() => handleLifecycle('resume')} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700">Resume</button>}
          </div>
        </div>
      </div>

      {/* Approval gate error banner */}
      {statusError && (
        <div className="mx-4 mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
          <svg className="w-4 h-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <span className="flex-1">{statusError}</span>
          <button onClick={() => setStatusError(null)} className="text-amber-400 hover:text-amber-600">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Board — feature sections with per-column task stacking */}
      <div className="flex-1 flex overflow-hidden">
        <UnassignedPanel items={unassignedTasks} features={allFeatures} onAdd={handleAddToSprint} locked={isLocked} />

        <div className="flex-1 overflow-auto">
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="min-w-[900px]">
              {/* Column headers */}
              <div className="grid sticky top-0 z-10 border-b border-gray-200" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)` }}>
                {COLUMNS.map(col => (
                  <div key={col} className={`px-3 py-2.5 ${COL_HEADER_BG[col]} border-r border-gray-200 last:border-r-0`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${STATUS_DOT[col]}`} />
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{COL_LABELS[col]}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {featureSections.length === 0 && totalTasks === 0 && (
                <div className="text-center py-12 text-sm text-gray-400">No items in this sprint yet.</div>
              )}

              {/* Feature sections — alternating band colors */}
              {featureSections.map((section, idx) => {
                const isExpanded = expandedFeatures.has(section.id);
                const bandBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/70';

                return (
                  <div key={section.id} className={`border-b border-gray-200 ${bandBg}`}>
                    {/* Feature header */}
                    <DraggableCell dragId={`fh:${section.id}`}>
                      <div className={`border-l-2 border-blue-400 cursor-grab active:cursor-grabbing relative ${isExpanded ? 'py-2 bg-blue-50' : 'py-1.5'}`}>
                        {/* Centered feature name */}
                        <div className="flex items-center justify-center">
                          {isExpanded ? (
                            <span className="text-sm font-medium text-blue-700 cursor-pointer hover:underline truncate max-w-[60%] text-center" onClick={() => navigate(`/employee/projects/${projectId}?view=feature-detail&featureId=${section.id}`)} onPointerDown={e => e.stopPropagation()}>
                              {section.title}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 truncate max-w-[60%] text-center" title={section.title}>
                              {section.title}
                            </span>
                          )}
                        </div>
                        {/* Left: chevron + sparkle */}
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                          <button onClick={e => { e.stopPropagation(); toggleFeature(section.id); }} onPointerDown={e => e.stopPropagation()} className="shrink-0">
                            <svg className={`w-3 h-3 text-blue-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                          </button>
                          <span title={section.title} className="cursor-help"><svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg></span>
                        </div>
                        {/* Right: count + SP + undo */}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                          <span className="text-xs text-gray-400">{section.taskCount}</span>
                          {section.sp > 0 && <span className="text-xs text-blue-600 font-medium">{section.sp} SP</span>}
                          {!isLocked && (
                            <button onClick={e => { e.stopPropagation(); handleRemoveFromSprint(section.id, 'feature'); }} onPointerDown={e => e.stopPropagation()} className="text-gray-400 hover:text-red-500" title="Return feature & tasks to unassigned">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </DraggableCell>

                    {/* Task grid — each column stacks this feature's tasks independently */}
                    {isExpanded && (
                      <div className="grid" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)` }}>
                        {COLUMNS.map(col => (
                          <DroppableCell key={col} id={`${col}:${section.id}`}>
                            <div className="border-r border-gray-100 last:border-r-0 min-h-[8px]">
                              {(section.tasksByCol[col] || []).map(task => (
                                <div key={task._id}>{renderTaskItem(task, task._itemType === 'bug' ? 'bug' : 'task', true)}</div>
                              ))}
                            </div>
                          </DroppableCell>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Standalone items (not in any feature) */}
              {hasStandalone && (
                <div className="border-b border-gray-200">
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Other tasks</span>
                  </div>
                  <div className="grid" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)` }}>
                    {COLUMNS.map(col => (
                      <DroppableCell key={col} id={`${col}:standalone`}>
                        <div className="border-r border-gray-100 last:border-r-0 min-h-[8px]">
                          {(standalonByCol[col] || []).map(task => (
                            <div key={task._id}>{renderTaskItem(task, task._itemType === 'bug' ? 'bug' : 'task', false)}</div>
                          ))}
                        </div>
                      </DroppableCell>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DragOverlay dropAnimation={null}>
              {getDragOverlayContent()}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {showIncomplete && sprintId && incompleteItems.length > 0 && (
        <IncompleteItemsModal
          sprintId={sprintId}
          items={incompleteItems}
          onClose={() => { setShowIncomplete(false); fetchBoard(); fetchUnassigned(); }}
        />
      )}

      {gateDialog && (
        <StageGateDialog
          taskId={gateDialog.taskId}
          taskTitle={gateDialog.taskTitle}
          projectId={projectId}
          targetStage={gateDialog.targetCol}
          gates={gateDialog.gates}
          projectMembers={projectMembers}
          onSubmitted={() => {
            completeGatedMove(gateDialog.taskId, gateDialog.targetCol);
            setGateDialog(null);
          }}
          onSkip={() => {
            completeGatedMove(gateDialog.taskId, gateDialog.targetCol);
            setGateDialog(null);
          }}
          onClose={() => setGateDialog(null)}
        />
      )}
    </div>
  );
};

export default SprintKanban;
